import cloneDeep from 'lodash/cloneDeep';

/**
 * Goal sections: which fields each picked goal renders.
 *
 * A goal section keeps a field list in `section.attributes`, and per-goal data
 * under `section.goals[goalId]`. Two shapes exist:
 *
 *  - OWNED (`goals[goalId].ownAttributes === true`): `goals[goalId].attributes`
 *    is the goal's own complete field list — type, label, properties, value.
 *    Adding, removing, reordering or retyping a field in one goal leaves the
 *    other goals alone. The section's `attributes` only seed a goal when it is
 *    picked (copied, never shared). Every progress report built from now on.
 *  - LEGACY (no flag): `goals[goalId].attributes[i]` is a sparse override merged
 *    onto `section.attributes[i]` by index. Reports saved before goals owned
 *    their fields, and fill hosts that never seed goals, still read this way.
 *
 * Targets are the same in both: `goals[goalId].targets[targetId].attributes[i]`
 * is an override merged onto the goal's field at `i`.
 *
 * The backend reads the same shapes through `goalFieldList` /
 * `forEachSectionAttribute` in @therapylake/package (form-builder/constants/
 * goalSections.ts — PDF, file walkers, sanitizers) and `templateWalk.ts` in the
 * api; members_client mirrors the read side in
 * fragments/formRenderer/utils/goalAttributes.js. Keep them in step. The
 * frontends keep their own copy because a legacy goal section here may borrow
 * another goal section's fields (`goalTemplateSection`), which the backend
 * does not.
 */

/** `override` merged onto `base`, properties included. */
export const mergeAttribute = (base, override) =>
    override
        ? { ...base, ...override, properties: { ...(base?.properties || {}), ...(override?.properties || {}) } }
        : base;

/** The section whose fields seed a goal section: its own, or the first goal section that has some. */
export const goalTemplateSection = (sections, section) =>
    section?.attributes?.length > 0 ? section : sections?.find((s) => s?.goalSection && s?.attributes?.length > 0) || section;

/** Whether this goal of the section owns its field list. */
export const isOwnedGoal = (section, goalId) => !!section?.goals?.[goalId]?.ownAttributes;

/** A goal's complete field list, whichever shape it is stored in. */
export const goalAttributes = (sections, sIndex, goalId) => {
    const section = sections?.[sIndex];
    const goal = section?.goals?.[goalId];
    if (goal?.ownAttributes) return goal.attributes || [];
    const template = goalTemplateSection(sections, section)?.attributes || [];
    return template.map((templateItem, index) => mergeAttribute(templateItem, goal?.attributes?.[index]));
};

/**
 * `next` section with every goal that owns its fields in `prev` still owning
 * them. A form→formList sync can carry a goal in the legacy shape when the form
 * state lagged behind `formList` (values written before it was re-seeded); its
 * values are folded onto the goal's own fields by index instead of replacing them.
 *
 * A goal removed from the section keeps its entry (as it always has), so
 * picking it again brings back its own fields and answers.
 */
export const keepOwnedGoals = (prev, next) => {
    const prevGoals = prev?.goals;
    if (!prevGoals || !next) return next;
    let goals = null;
    Object.entries(prevGoals).forEach(([goalId, prevGoal]) => {
        const nextGoal = next.goals?.[goalId];
        if (!prevGoal?.ownAttributes || nextGoal?.ownAttributes) return;
        goals = goals || { ...(next.goals || {}) };
        goals[goalId] = {
            ...prevGoal,
            ...(nextGoal || {}),
            ownAttributes: true,
            attributes: (prevGoal.attributes || []).map((attr, index) => mergeAttribute(attr, nextGoal?.attributes?.[index])),
        };
    });
    return goals ? { ...next, goals } : next;
};

/**
 * Every picked goal of every goal section, made to own its fields: a deep copy
 * of the section's fields with any legacy override folded in. Goals that
 * already own theirs are left as they are. Returns `formList` itself when
 * nothing needed seeding, so callers can use it inside a state updater freely.
 */
export const seedGoalAttributes = (formList) => {
    const sections = formList?.sections;
    if (!Array.isArray(sections)) return formList;

    let changed = false;
    const nextSections = sections.map((section, sIndex) => {
        if (!section?.goalSection || !section?.selectedGoals?.length) return section;
        const missing = section.selectedGoals.filter((goal) => goal?.id && !isOwnedGoal(section, goal.id));
        if (!missing.length) return section;

        changed = true;
        const goals = { ...(section.goals || {}) };
        missing.forEach((goal) => {
            goals[goal.id] = {
                ...(goals[goal.id] || {}),
                ownAttributes: true,
                attributes: cloneDeep(goalAttributes(sections, sIndex, goal.id)),
            };
        });
        return { ...section, goals };
    });

    return changed ? { ...formList, sections: nextSections } : formList;
};
